import React, { useState } from "react";
import { Typography, Button, Container, Select as MuiSelect, MenuItem, Snackbar, Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, Checkbox, FormControlLabel, FormControl, InputLabel, Tooltip, Box } from "@mui/material";
import { calculateWeeklyScores } from "../../../lib/scoring";
import { getFunctions, httpsCallable } from "firebase/functions";
import { utils, writeFile } from "xlsx";
import { useData } from "../../../contexts/DataContext";
import { MAX_WEEK } from "./useAdminWeek";

// Controlled: the season/week selection belongs to the parent (ScoringPage
// keeps it in the URL so it is shared with the Games tab).
function CalculateScores({ seasons, season, week, onSeasonChange, onWeekChange }) {
  const { setWinningTeams, currentSeason } = useData();
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // When checked, publishing also fires the leaderboard-update SMS to opted-in
  // users. Defaults OFF so corrections / re-publishes don't re-notify unless
  // the admin explicitly opts in each time.
  const [notifyOnPublish, setNotifyOnPublish] = useState(false);

  // notifyLeaderboardUpdate only takes a week; the server resolves the season
  // from the active one. Texting about a non-active season would announce
  // the wrong leaderboard, so the option is only offered on the active season.
  const isActiveSeason = Boolean(season) && season === currentSeason;

  const handlePreviewScores = async () => {
    if (!season || !week) {
      setError("Please select a year and a week.");
      return;
    }
    setIsPreviewLoading(true);
    setError(null);
    setPreviewData(null);
    try {
      const results = await calculateWeeklyScores(season, week);
      setPreviewData(results.weeklyScores);
      setWinningTeams(results.winningTeams);
    } catch (err) {
      setError(err.message);
      console.error("Error calculating scores:", err);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handlePublishScores = async () => {
    if (!previewData) {
      setError("Please generate a preview before publishing.");
      return;
    }
    const publishedWeek = week;
    setError(null);
    setIsPublishing(true);
    try {
      // Publishing runs server-side: security rules deny direct client writes
      // to /picks, so the write-back stamping winning picks can only happen
      // through the Admin SDK inside the publishScores callable.
      const publish = httpsCallable(getFunctions(), "publishScores");
      const publishResult = await publish({ season: String(season), week: publishedWeek });
      const published = (publishResult.data && publishResult.data.weeklyScores) || {};

      let message = "Scores published successfully!";
      let severity = "success";

      // The server recomputes from Firestore at publish time. If game data
      // changed after the preview was generated, the published totals differ
      // from the table the admin approved — surface that instead of hiding it.
      const divergedUsers = Object.keys(published).filter(
        (userId) => !previewData[userId] || previewData[userId].total !== published[userId].total
      );
      if (divergedUsers.length > 0) {
        message = `Scores published, but totals for ${divergedUsers.length} user(s) differ from the preview (game data changed since it was generated). Re-run Calculate & Preview to review.`;
        severity = "warning";
      }

      // Optional: notify opted-in users. Decoupled from the publish itself —
      // an SMS failure must NOT roll back or fail the publish (scores are
      // already saved), so we warn rather than throw.
      if (notifyOnPublish && isActiveSeason) {
        try {
          const notify = httpsCallable(getFunctions(), "notifyLeaderboardUpdate");
          const result = await notify({ week: publishedWeek });
          const { sent = 0, recipientCount = 0, failed = 0 } = result.data || {};
          message += ` Sent ${sent} of ${recipientCount} leaderboard notifications.${failed > 0 ? ` ${failed} failed.` : ""}`;
          if (failed > 0) severity = "warning";
        } catch (notifyErr) {
          console.error("Scores published but leaderboard notification failed:", notifyErr);
          message += ` But the SMS notification failed (${notifyErr.message || "unknown error"}). Scores are saved.`;
          severity = "warning";
        }
      }

      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
      // The selection stays put (it is shared with the Games tab); only the
      // approved preview is cleared.
      setPreviewData(null);
      setNotifyOnPublish(false);
    } catch (err) {
      setError(err.message);
      console.error("Error publishing scores:", err);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDownloadResults = () => {
    if (!previewData) {
      setError("Please generate a preview before downloading.");
      return;
    }

    const dataForSheet = Object.entries(previewData).map(([userId, scores]) => ({
      User: scores.name || userId,
      "Pick Winner Points": scores.pickWinnerPoints,
      "Pick Score Points": scores.pickScorePoints,
      "Total Weekly Points": scores.total,
    }));

    const worksheet = utils.json_to_sheet(dataForSheet);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Weekly Scores");
    writeFile(workbook, `Week_${week}_Scores.xlsx`);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const notifyControl = (
    <FormControlLabel
      sx={{ mt: 2, display: 'block' }}
      control={
        <Checkbox
          checked={notifyOnPublish && isActiveSeason}
          disabled={!isActiveSeason}
          onChange={(e) => setNotifyOnPublish(e.target.checked)}
        />
      }
      label={`Text opted-in users that Week ${week} results are posted`}
    />
  );

  return (
    <Container maxWidth="md" style={{ marginTop: '40px', marginBottom: '40px' }}>
      <Typography variant="h4" align="center" gutterBottom>
        Calculate Weekly Scores
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel id="scoring-season-label">Season</InputLabel>
            <MuiSelect
              labelId="scoring-season-label"
              label="Season"
              value={seasons.includes(season) ? season : ""}
              onChange={(e) => onSeasonChange(e.target.value)}
            >
              {seasons.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel id="scoring-week-label">Week</InputLabel>
            <MuiSelect
              labelId="scoring-week-label"
              label="Week"
              value={week ?? ""}
              onChange={(e) => onWeekChange(e.target.value)}
            >
              {[...Array(MAX_WEEK).keys()].map((w) => (
                <MenuItem key={w + 1} value={w + 1}>
                  {w + 1}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePreviewScores}
            loading={isPreviewLoading}
            fullWidth
            sx={{ height: 56 }}
          >
            Calculate & Preview
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" style={{ marginTop: '20px' }}>
          {error}
        </Alert>
      )}

      {previewData && (
        <div style={{ marginTop: '20px' }}>
          <Typography variant="h6">Preview of Weekly Scores</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Pick Winner Points</TableCell>
                  <TableCell>Pick Score Points</TableCell>
                  <TableCell>Total Weekly Points</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(previewData).map(([userId, scores]) => (
                  <TableRow key={userId}>
                    <TableCell>{scores.name || userId}</TableCell>
                    <TableCell>{scores.pickWinnerPoints}</TableCell>
                    <TableCell>{scores.pickScorePoints}</TableCell>
                    <TableCell>{scores.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {isActiveSeason ? notifyControl : (
            <Tooltip title={`Texts go out for the active season (${currentSeason}) only, because the notification is always about the current leaderboard.`}>
              <Box component="span" sx={{ display: 'inline-block' }}>{notifyControl}</Box>
            </Tooltip>
          )}
          <Grid container spacing={2} style={{ marginTop: '8px' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handlePublishScores}
                loading={isPublishing}
                fullWidth
              >
                Confirm & Publish Scores
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleDownloadResults}
                fullWidth
              >
                Download Results
              </Button>
            </Grid>
          </Grid>
        </div>
      )}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default CalculateScores;
